export type Locale = "en" | "pt" | "de"

export const locales: Locale[] = ["en", "pt", "de"]
export const defaultLocale: Locale = "en"

export const localeLabels: Record<Locale, string> = {
  en: "EN",
  pt: "PT",
  de: "DE",
}

type Content = {
  meta: {
    title: string
    description: string
  }
  nav: {
    home: string
    focus: string
    journey: string
    projects: string
    contact: string
    cv: string
    proof: string
  }
  hero: {
    greeting: string
    name: string
    role: string
    tagline: string
    description: string
    available: string
    ctaCv: string
    facts: { label: string; value: string }[]
    stackLabel: string
    stats: { n: number; suffix: string; label: string }[]
  }
  proof: {
    title: string
    lead: string
    items: { figure: string; detail: string }[]
  }
  focus: {
    title: string
    lead: string
    items: { label: string; detail: string }[]
  }
  journey: {
    title: string
    lead: string
    showMore: string
    showLess: string
    entries: {
      year: string
      title: string
      organization: string
      description: string
    }[]
  }
  projects: {
    title: string
    lead: string
    viewProject: string
    featuredLabel: string
    moreLabel: string
    statusActive: string
    statusArchived: string
    statusInProgress: string
  }
  contact: {
    title: string
    description: string
    cta: string
    email: string
  }
  footer: {
    statement: string
    rights: string
  }
  alt: {
    contents: string
    skills: string
    skillCore: string
    skillPlatform: string
    skillAlso: string
    education: string
    certifications: string
    languages: string
  }
}

/** The stack a recruiter scans for. Same words in every language. */
export const coreStack = [
  "Java",
  "Spring Boot",
  "PostgreSQL",
  "Redis",
  "Apache Kafka",
  "Docker",
  "Kubernetes",
  "Grafana",
]

/** Skill groups, straight from the CV. Values read the same in every language. */
export const skillGroups = [
  {
    key: "core" as const,
    items:
      "Java · Spring Boot · Spring Security · JPA/Hibernate · REST APIs · PostgreSQL · Redis · Apache Kafka · RabbitMQ",
  },
  {
    key: "platform" as const,
    items:
      "Docker · Kubernetes · GitLab CI · SonarQube · Grafana · Prometheus · JUnit · Mockito · Swagger/OpenAPI · Git",
  },
  {
    key: "also" as const,
    items:
      "TypeScript · React · Next.js · Claude Code and local LLM agents · Go · Rust · Tauri",
  },
]

export const education: { degree: string; school: string; years: string }[] = [
  {
    degree: "MSc Informatics Engineering & Web Technology",
    school: "Universidade Aberta",
    years: "2024 – present",
  },
  {
    degree: "Graduate Coursework in Software Engineering",
    school: "Instituto Superior de Engenharia do Porto",
    years: "2023 – 2024",
  },
  {
    degree: "BSc Communication and Multimedia",
    school: "Universidade de Trás-os-Montes e Alto Douro",
    years: "2020 – 2023",
  },
]

export const certifications = "CMMI · SOLID Architecture · Java Development Course"

export const content: Record<Locale, Content> = {
  en: {
    meta: {
      title: "Luís Peixoto · Backend Software Engineer | Java, Spring Boot & Distributed Systems",
      description:
        "Luís Peixoto, backend software engineer in Portugal. Owns Java & Spring Boot microservices at scale: 100M+ records, zero data loss. Open to remote or relocation.",
    },
    nav: {
      home: "Home",
      focus: "Focus",
      journey: "Journey",
      projects: "Projects",
      contact: "Contact",
      cv: "CV",
      proof: "Evidence",
    },
    hero: {
      greeting: "Hello, I am",
      name: "Luís Peixoto",
      role: "Backend Software Engineer at CEiiA",
      tagline: "I make the impossible boring",
      description:
        "I build and own the backend systems that keep large platforms running, and I fix the problems others get stuck on. My favorite work is taking the systems nobody wants to touch and making them fast, secure, and boringly reliable.",
      available: "Open to mid-level backend roles, relocating or fully remote",
      ctaCv: "Read the CV",
      stackLabel: "Core stack",
      facts: [
        { label: "Based", value: "Vila Real, Portugal" },
        { label: "Permit", value: "EU citizen, no sponsorship needed" },
        { label: "Languages", value: "Portuguese native, English C1" },
      ],
      stats: [
        { n: 3, suffix: "+", label: "Years at CEiiA" },
        { n: 15, suffix: "+", label: "Microservices owned" },
        { n: 1, suffix: "M+", label: "Trips validated each month" },
        { n: 100, suffix: "M+", label: "Records processed" },
      ],
    },
    proof: {
      title: "Evidence",
      lead: "Four things I have actually done, with the numbers attached.",
      items: [
        {
          figure: "Zero data loss",
          detail:
            "Every senior engineer on the platform left. I held all 15 microservices on my own for about six months: 20+ production incidents resolved, millions of trips validated without interruption, and new work still shipping.",
        },
        {
          figure: "Every request faster",
          detail:
            "Authentication re-ran on every single request. I moved token storage into Redis and cut latency on every request from about 300ms to 14ms, roughly twenty times faster, across the whole platform.",
        },
        {
          figure: "100M+ records",
          detail:
            "I build and own 15+ Java and Spring Boot microservices behind platforms that process hundreds of millions of records and validate over a million trips a month, on Kafka, Redis, PostgreSQL, Docker and Kubernetes.",
        },
        {
          figure: "Hours, not days",
          detail:
            "I wire AI agents into daily engineering. A report a colleague spent 30 minutes on every day now writes itself, a full microservice test suite lands in hours instead of days, and new projects scaffold in minutes.",
        },
      ],
    },
    focus: {
      title: "What I do",
      lead: "Four things I am paid to be good at, and the evidence behind each.",
      items: [
        {
          label: "High-Scale Backend & Distributed Systems",
          detail:
            "Java and Spring Boot microservices behind platforms that process hundreds of millions of records and validate millions of trips a month, using Kafka, Redis, PostgreSQL, Docker, and Kubernetes.",
        },
        {
          label: "Monoliths → Fast, Secure Microservices",
          detail:
            "The thing I do best: turning tangled monoliths into clean, fast, scalable services, with security built in. One authentication redesign alone cut every request from ~300ms to ~14ms.",
        },
        {
          label: "AI-Augmented Engineering",
          detail:
            "I wire AI agents deep into my workflow (Claude Code plus local models) to automate reports, generate full test suites in hours, and scaffold new projects in minutes.",
        },
        {
          label: "Sustainability & Mobility Tech",
          detail:
            "Core engineer on AYR, CEiiA's award-winning platform (New European Bauhaus prize) that turns CO₂ avoided into digital green credits for citizens, cities, and partners.",
        },
      ],
    },
    journey: {
      title: "How I got here",
      lead: "Intern to platform owner in three years, plus the studying that ran alongside it.",
      showMore: "Show full journey",
      showLess: "Show fewer entries",
      entries: [
        {
          year: "2023 – Present",
          title: "Backend Software Engineer",
          organization: "CEiiA, Centre of Engineering and Product Development, Matosinhos",
          description:
            "I own high-scale Java and Spring Boot microservices across multiple teams, from data ingestion to AYR, CEiiA's award-winning sustainability platform. When the senior team left, I held the full platform alone for about 6 months: zero data loss, no interruption. Now I drive cross-team technical direction alongside the platform architect.",
        },
        {
          year: "2022 – 2023",
          title: "Academic Digital Innovation",
          organization: "Associação Académica da UTAD & Student Technology Chapter",
          description:
            "Led digital transition projects, coordinated the technology department, and shipped event sites plus communication tools for the student community.",
        },
        {
          year: "2022",
          title: "Frontend Developer (Internship)",
          organization: "X-Plora",
          description:
            "Built responsive front-end experiences and component libraries in Vue.js, where my frontend roots began.",
        },
        {
          year: "2024 – Present",
          title: "Master's in Informatics Engineering & Web Technology",
          organization: "Universidade Aberta",
          description:
            "Ongoing master's focused on advanced software engineering, distributed systems, and modern web technology.",
        },
        {
          year: "2023 – 2024",
          title: "Graduate Coursework in Software Engineering",
          organization: "Instituto Superior de Engenharia do Porto",
          description:
            "Studied software architecture, quality, and project delivery methodologies.",
        },
        {
          year: "2020 – 2023",
          title: "Bachelor's in Communication and Multimedia",
          organization: "Universidade de Trás-os-Montes e Alto Douro",
          description:
            "Blended multimedia production with software. It's the reason I care about the people behind every system, not just the architecture.",
        },
        {
          year: "2015 – 2018",
          title: "IT Systems Management and Programming Technician",
          organization: "Escola EB 2/3 de Mondim de Basto",
          description:
            "Technical training in information systems, programming, and systems maintenance.",
        },
      ],
    },
    projects: {
      title: "Built on my own time",
      lead: "Side projects that are deployed, used, and maintained. Not demos.",
      viewProject: "View project",
      featuredLabel: "Featured",
      moreLabel: "Also built",
      statusActive: "Active",
      statusArchived: "Archived",
      statusInProgress: "In progress",
    },
    contact: {
      title: "Let's talk",
      description:
        "Open to mid-level backend roles, relocation or full remote. Always happy to talk distributed systems, reliability, and the hard problems that don't have an obvious answer. Drop me a line.",
      cta: "Send me an email",
      email: "peixotomdb@gmail.com",
    },
    footer: {
      statement: "Backend engineer who owns high-scale systems end to end, and treats reliability as the product.",
      rights: "All rights reserved.",
    },
    alt: {
      contents: "Contents",
      skills: "Technical skills",
      skillCore: "Core",
      skillPlatform: "Platform",
      skillAlso: "Also",
      education: "Education",
      certifications: "Certifications",
      languages: "Languages",
    },
  },
  pt: {
    meta: {
      title: "Luís Peixoto · Backend Software Engineer | Java, Spring Boot & Sistemas Distribuídos",
      description:
        "Luís Peixoto, backend software engineer em Portugal. Microserviços Java e Spring Boot em grande escala: 100M+ registos, zero perda de dados. Remoto ou relocação.",
    },
    nav: {
      home: "Início",
      focus: "Foco",
      journey: "Percurso",
      projects: "Projetos",
      contact: "Contacto",
      cv: "CV",
      proof: "Provas",
    },
    hero: {
      greeting: "Olá, sou o",
      name: "Luís Peixoto",
      role: "Backend Software Engineer no CEiiA",
      tagline: "I make the impossible boring",
      description:
        "Construo e sou dono dos sistemas backend que mantêm grandes plataformas a funcionar, e resolvo os problemas em que os outros ficam presos. O que mais gosto é pegar nos sistemas em que ninguém quer tocar e torná-los rápidos, seguros e aborrecidamente fiáveis.",
      available: "Aberto a funções de backend de nível intermédio, com relocação ou full remote",
      ctaCv: "Ver o CV",
      stackLabel: "Stack principal",
      facts: [
        { label: "Base", value: "Vila Real, Portugal" },
        { label: "Autorização", value: "Cidadão da UE, sem patrocínio" },
        { label: "Línguas", value: "Português nativo, Inglês C1" },
      ],
      stats: [
        { n: 3, suffix: "+", label: "Anos no CEiiA" },
        { n: 15, suffix: "+", label: "Microserviços que mantenho" },
        { n: 1, suffix: "M+", label: "Viagens validadas por mês" },
        { n: 100, suffix: "M+", label: "Registos processados" },
      ],
    },
    proof: {
      title: "Provas",
      lead: "Quatro coisas que fiz mesmo, com os números ao lado.",
      items: [
        {
          figure: "Zero perda de dados",
          detail:
            "Todos os engenheiros seniores da plataforma saíram. Mantive os 15 microserviços sozinho durante cerca de seis meses: mais de 20 incidentes de produção resolvidos, milhões de viagens validadas sem interrupção, e desenvolvimento novo a sair na mesma.",
        },
        {
          figure: "Cada pedido mais rápido",
          detail:
            "A autenticação repetia-se em cada pedido. Movi os tokens para Redis e cortei a latência de cada pedido de cerca de 300ms para 14ms, umas vinte vezes mais rápido, em toda a plataforma.",
        },
        {
          figure: "100M+ registos",
          detail:
            "Construo e mantenho mais de 15 microserviços Java e Spring Boot por trás de plataformas que processam centenas de milhões de registos e validam mais de um milhão de viagens por mês, com Kafka, Redis, PostgreSQL, Docker e Kubernetes.",
        },
        {
          figure: "Horas, não dias",
          detail:
            "Integro agentes de IA na engenharia do dia a dia. Um relatório que ocupava 30 minutos diários a um colega escreve-se agora sozinho, uma suite de testes completa de um microserviço sai em horas em vez de dias, e projetos novos nascem em minutos.",
        },
      ],
    },
    focus: {
      title: "O que faço",
      lead: "Quatro coisas em que sou pago para ser bom, e a evidência de cada uma.",
      items: [
        {
          label: "Backend de Grande Escala & Sistemas Distribuídos",
          detail:
            "Microserviços Java e Spring Boot por trás de plataformas que processam centenas de milhões de registos e validam milhões de viagens por mês, com Kafka, Redis, PostgreSQL, Docker e Kubernetes.",
        },
        {
          label: "Monólitos → Microserviços Rápidos e Seguros",
          detail:
            "É o que faço melhor: transformar monólitos complexos em serviços limpos, rápidos e escaláveis, com segurança de raiz. Um redesenho da autenticação cortou cada pedido de ~300ms para ~14ms.",
        },
        {
          label: "Engenharia Aumentada por IA",
          detail:
            "Integro agentes de IA no meu fluxo de trabalho (Claude Code e modelos locais) para automatizar relatórios, gerar suites de testes completas em horas e criar novos projetos em minutos.",
        },
        {
          label: "Tecnologia de Sustentabilidade & Mobilidade",
          detail:
            "Engenheiro-chave no AYR, a plataforma premiada da CEiiA (prémio New European Bauhaus) que transforma o CO₂ evitado em créditos verdes digitais para cidadãos, cidades e parceiros.",
        },
      ],
    },
    journey: {
      title: "Como cheguei aqui",
      lead: "De estagiário a dono da plataforma em três anos, com os estudos a correr em paralelo.",
      showMore: "Ver percurso completo",
      showLess: "Ver menos",
      entries: [
        {
          year: "2023 – Presente",
          title: "Backend Software Engineer",
          organization: "CEiiA, Centre of Engineering and Product Development, Matosinhos",
          description:
            "Sou dono de microserviços Java e Spring Boot de grande escala em várias equipas, da ingestão de dados ao AYR, a plataforma de sustentabilidade premiada da CEiiA. Quando a equipa sénior saiu, mantive a plataforma sozinho durante cerca de 6 meses: zero perda de dados, sem interrupções. Agora dirijo decisões técnicas cross-team ao lado do arquiteto da plataforma.",
        },
        {
          year: "2022 – 2023",
          title: "Inovação Digital Académica",
          organization: "Associação Académica da UTAD & Núcleo de Comunicação e Multimédia",
          description:
            "Liderei a transição digital, coordenei o departamento de tecnologia e lancei websites e ferramentas de comunicação para a comunidade estudantil.",
        },
        {
          year: "2022",
          title: "Frontend Developer (Estágio)",
          organization: "X-Plora",
          description:
            "Construí experiências frontend responsivas e libraries de componentes em Vue.js, onde começaram as minhas raízes de frontend.",
        },
        {
          year: "2024 – Presente",
          title: "Mestrado em Engenharia Informática e Tecnologia Web",
          organization: "Universidade Aberta",
          description:
            "Mestrado a decorrer, focado em engenharia de software avançada, sistemas distribuídos e tecnologia web moderna.",
        },
        {
          year: "2023 – 2024",
          title: "Estudos de Pós-Graduação em Engenharia de Software",
          organization: "Instituto Superior de Engenharia do Porto",
          description:
            "Estudos em arquitetura de software, qualidade e metodologias de entrega de projetos.",
        },
        {
          year: "2020 – 2023",
          title: "Licenciatura em Comunicação e Multimédia",
          organization: "Universidade de Trás-os-Montes e Alto Douro",
          description:
            "Uni produção multimédia e software. É a razão pela qual me preocupo com as pessoas por trás de cada sistema, não só com a arquitetura.",
        },
        {
          year: "2015 – 2018",
          title: "Técnico de Gestão e Programação de Sistemas Informáticos",
          organization: "Escola EB 2/3 de Mondim de Basto",
          description:
            "Formação técnica em sistemas de informação, programação e manutenção de sistemas.",
        },
      ],
    },
    projects: {
      title: "Feito no meu tempo",
      lead: "Projetos pessoais publicados, usados e mantidos. Não são demos.",
      viewProject: "Ver projeto",
      featuredLabel: "Em destaque",
      moreLabel: "Também construí",
      statusActive: "Ativo",
      statusArchived: "Arquivado",
      statusInProgress: "Em curso",
    },
    contact: {
      title: "Vamos falar",
      description:
        "Aberto a posições de backend de nível intermédio, com relocação ou full remote. Sempre pronto para falar de sistemas distribuídos, fiabilidade e dos problemas difíceis que não têm resposta óbvia. Manda-me uma mensagem.",
      cta: "Manda-me um email",
      email: "peixotomdb@gmail.com",
    },
    footer: {
      statement: "Engenheiro backend que é dono de sistemas de grande escala de ponta a ponta, e trata a fiabilidade como o produto.",
      rights: "Todos os direitos reservados.",
    },
    alt: {
      contents: "Índice",
      skills: "Competências técnicas",
      skillCore: "Base",
      skillPlatform: "Plataforma",
      skillAlso: "Também",
      education: "Formação",
      certifications: "Certificações",
      languages: "Línguas",
    },
  },
  de: {
    meta: {
      title: "Luís Peixoto · Backend Software Engineer | Java, Spring Boot & Verteilte Systeme",
      description:
        "Luís Peixoto, Backend Software Engineer aus Portugal. Java- und Spring-Boot-Microservices im großen Maßstab: 100M+ Datensätze, null Datenverlust. Remote oder Umzug.",
    },
    nav: {
      home: "Start",
      focus: "Fokus",
      journey: "Werdegang",
      projects: "Projekte",
      contact: "Kontakt",
      cv: "Lebenslauf",
      proof: "Belege",
    },
    hero: {
      greeting: "Hallo, ich bin",
      name: "Luís Peixoto",
      role: "Backend Software Engineer bei CEiiA",
      tagline: "I make the impossible boring",
      description:
        "Ich baue und verantworte die Backend-Systeme, die große Plattformen am Laufen halten, und ich löse die Probleme, an denen andere scheitern. Am liebsten nehme ich die Systeme, die niemand anfassen will, und mache sie schnell, sicher und langweilig zuverlässig.",
      available: "Offen für Backend-Rollen auf Mid-Level, mit Umzug oder voll remote",
      ctaCv: "Lebenslauf lesen",
      stackLabel: "Kern-Stack",
      facts: [
        { label: "Standort", value: "Vila Real, Portugal" },
        { label: "Arbeitserlaubnis", value: "EU-Bürger, kein Sponsoring nötig" },
        { label: "Sprachen", value: "Portugiesisch Muttersprache, Englisch C1" },
      ],
      stats: [
        { n: 3, suffix: "+", label: "Jahre bei CEiiA" },
        { n: 15, suffix: "+", label: "Microservices verantwortet" },
        { n: 1, suffix: "M+", label: "Fahrten pro Monat validiert" },
        { n: 100, suffix: "M+", label: "Datensätze verarbeitet" },
      ],
    },
    proof: {
      title: "Belege",
      lead: "Vier Dinge, die ich wirklich getan habe, mit den Zahlen dazu.",
      items: [
        {
          figure: "Null Datenverlust",
          detail:
            "Alle Senior-Engineers der Plattform gingen. Ich hielt alle 15 Microservices rund sechs Monate allein am Laufen: über 20 Produktionsvorfälle gelöst, Millionen Fahrten ohne Unterbrechung validiert, und neue Entwicklung lief weiter.",
        },
        {
          figure: "Jede Anfrage schneller",
          detail:
            "Die Authentifizierung lief bei jeder Anfrage erneut. Ich verlagerte die Tokens nach Redis und senkte die Latenz jeder Anfrage plattformweit von rund 300ms auf 14ms, etwa zwanzigmal schneller.",
        },
        {
          figure: "100M+ Datensätze",
          detail:
            "Ich baue und verantworte über 15 Java- und Spring-Boot-Microservices hinter Plattformen, die Hunderte Millionen Datensätze verarbeiten und über eine Million Fahrten pro Monat validieren, mit Kafka, Redis, PostgreSQL, Docker und Kubernetes.",
        },
        {
          figure: "Stunden statt Tage",
          detail:
            "Ich baue KI-Agenten in die tägliche Entwicklung ein. Ein Report, für den ein Kollege täglich 30 Minuten brauchte, schreibt sich jetzt selbst, eine komplette Testsuite entsteht in Stunden statt Tagen, neue Projekte in Minuten.",
        },
      ],
    },
    focus: {
      title: "Was ich mache",
      lead: "Vier Dinge, für die ich bezahlt werde, und der Beleg für jedes davon.",
      items: [
        {
          label: "Hochskalierbares Backend & Verteilte Systeme",
          detail:
            "Java- und Spring-Boot-Microservices hinter Plattformen, die Hunderte Millionen Datensätze verarbeiten und Millionen Fahrten pro Monat validieren, mit Kafka, Redis, PostgreSQL, Docker und Kubernetes.",
        },
        {
          label: "Monolithen → schnelle, sichere Microservices",
          detail:
            "Das, was ich am besten kann: verworrene Monolithen in saubere, schnelle und skalierbare Services verwandeln, mit Sicherheit von Grund auf. Ein Redesign der Authentifizierung senkte jede Anfrage von ~300ms auf ~14ms.",
        },
        {
          label: "KI-gestützte Entwicklung",
          detail:
            "Ich integriere KI-Agenten tief in meinen Workflow (Claude Code und lokale Modelle), um Reports zu automatisieren, komplette Testsuites in Stunden zu erstellen und neue Projekte in Minuten aufzusetzen.",
        },
        {
          label: "Nachhaltigkeits- & Mobilitätstechnologie",
          detail:
            "Kern-Entwickler von AYR, CEiiAs preisgekrönter Plattform (New European Bauhaus Prize), die vermiedenes CO₂ in digitale grüne Guthaben für Bürger, Städte und Partner verwandelt.",
        },
      ],
    },
    journey: {
      title: "Wie ich hierher kam",
      lead: "Vom Praktikanten zum Plattform-Verantwortlichen in drei Jahren, mit parallel laufendem Studium.",
      showMore: "Kompletten Werdegang anzeigen",
      showLess: "Weniger anzeigen",
      entries: [
        {
          year: "2023 – Heute",
          title: "Backend Software Engineer",
          organization: "CEiiA, Centre of Engineering and Product Development, Matosinhos",
          description:
            "Ich verantworte hochskalierbare Java- und Spring-Boot-Microservices in mehreren Teams, von der Datenaufnahme bis zu AYR, CEiiAs preisgekrönter Nachhaltigkeitsplattform. Als das Senior-Team ging, hielt ich die gesamte Plattform rund 6 Monate allein am Laufen: null Datenverlust, keine Unterbrechung. Heute treibe ich die technische Ausrichtung teamübergreifend mit dem Plattform-Architekten voran.",
        },
        {
          year: "2022 – 2023",
          title: "Akademische digitale Innovation",
          organization: "Associação Académica da UTAD & Technologiekapitel",
          description:
            "Steuerte digitale Transformationsprojekte, koordinierte die Technologieabteilung und lieferte Event-Websites sowie Kommunikations-Tools für die Studierendenschaft.",
        },
        {
          year: "2022",
          title: "Frontend-Entwickler (Praktikum)",
          organization: "X-Plora",
          description:
            "Baute responsive Frontend-Erlebnisse und Komponentenbibliotheken in Vue.js, dem Ursprung meiner Frontend-Wurzeln.",
        },
        {
          year: "2024 – Heute",
          title: "Master in Informatikingenieurwesen & Webtechnologie",
          organization: "Universidade Aberta",
          description:
            "Laufendes Masterstudium mit Fokus auf fortgeschrittene Softwaretechnik, verteilte Systeme und moderne Webtechnologien.",
        },
        {
          year: "2023 – 2024",
          title: "Aufbaustudium in Software Engineering",
          organization: "Instituto Superior de Engenharia do Porto",
          description:
            "Studium von Softwarearchitektur, Qualität und Projekt-Delivery-Methoden.",
        },
        {
          year: "2020 – 2023",
          title: "Bachelor in Kommunikation und Multimedia",
          organization: "Universidade de Trás-os-Montes e Alto Douro",
          description:
            "Verband Multimedia-Produktion mit Software. Das ist der Grund, warum mir die Menschen hinter jedem System wichtig sind, nicht nur die Architektur.",
        },
        {
          year: "2015 – 2018",
          title: "Techniker für IT-Systemverwaltung und Programmierung",
          organization: "Escola EB 2/3 de Mondim de Basto",
          description:
            "Technische Ausbildung in Informationssystemen, Programmierung und Systemwartung.",
        },
      ],
    },
    projects: {
      title: "In meiner Freizeit gebaut",
      lead: "Eigene Projekte, veröffentlicht, genutzt und gepflegt. Keine Demos.",
      viewProject: "Projekt ansehen",
      featuredLabel: "Hervorgehoben",
      moreLabel: "Außerdem gebaut",
      statusActive: "Aktiv",
      statusArchived: "Archiviert",
      statusInProgress: "In Arbeit",
    },
    contact: {
      title: "Sprechen wir",
      description:
        "Offen für Backend-Rollen auf Mid-Level, mit Umzug oder voll remote. Immer offen für ein Gespräch über verteilte Systeme, Zuverlässigkeit und die kniffligen Probleme ohne offensichtliche Lösung. Schreib mir.",
      cta: "Schreib mir",
      email: "peixotomdb@gmail.com",
    },
    footer: {
      statement:
        "Backend-Engineer, der hochskalierbare Systeme von Anfang bis Ende verantwortet und für den Zuverlässigkeit das Produkt ist.",
      rights: "Alle Rechte vorbehalten.",
    },
    alt: {
      contents: "Inhalt",
      skills: "Technische Kenntnisse",
      skillCore: "Kern",
      skillPlatform: "Plattform",
      skillAlso: "Außerdem",
      education: "Ausbildung",
      certifications: "Zertifikate",
      languages: "Sprachen",
    },
  },
}
